import { useI18n } from '@/i18n/i18n-context'
import { cn } from '@/lib/cn'
import { Card } from '@/ui/card'
import { FaceGuideMask } from '@/ui/face-guide-mask'
import { ScanStatusWash, type ScanState } from '@/ui/scan-status-wash'
import type { ScanEventDescription } from '@/sdk/fh-vitals-events'
import type { ScanVendor } from '@/api/types'
import type { MindsetPhase } from '@/sdk/mindset-session'

export const SCAN_CANVAS_ID = 'fh-canvas'
export const SCAN_VIDEO_ID = 'mindset-video'

interface Props {
  /**
   * Every signal the vendor is raising right now, in the order it raised them.
   *
   * A LIST because the vendor sends several at once, and RED below the instruction because that is what
   * they are: the things standing between this patient and a reading (docs/decisions.md D-28). The
   * FaceHeart path passes at most one, which is a list of one.
   */
  readonly troubles: readonly ScanEventDescription[]
  /**
   * How far through the measurement the VENDOR says it is, as a fraction of one, or null while it has
   * not said anything yet - which is every frame of a FaceHeart scan, because that SDK emits no such
   * signal at all (see the bar below).
   */
  readonly progress: number | null
  readonly vendor: ScanVendor
  /**
   * Which phase the vendor session is in, or null before it has said (and on the FaceHeart path, which
   * reports none).
   *
   * On the screen because a phone has no console. The bar cannot carry this: it only moves once the
   * VENDOR starts ticking, which on a phone is a minute after the patient tapped - so "loading" and
   * "stuck" were the same picture, and the fault we were chasing on an iPhone was invisible to the
   * person holding it (docs/decisions.md D-26).
   */
  readonly phase?: MindsetPhase | null
  /**
   * The camera's own width over height, or null when nothing has reported it.
   *
   * Only Shen.AI's surface reads it, and only to crop - see the canvas in that branch below.
   */
  readonly previewAspect?: number | null
}

/**
 * The preview frame's shape, and the field face-guide-mask.tsx's artwork is drawn on. The two are one
 * decision: a circle drawn for a portrait frame lands on the patient's shoulder in any other.
 */
const FRAME_ASPECT = 315 / 382

/**
 * Purely presentational - the session itself runs in ScanFlow's effect, driven by an injected client
 * so this screen never talks to an SDK directly.
 *
 * The vendors need two different elements and there is no way around it. FaceHeart and Shen.AI
 * each draw their own preview onto a `<canvas>` they are handed the id of; Mindset reads frames
 * off an `<video>` element that this app has already put a camera stream on
 * (browser-mindset-client.ts). Rendering both and hiding one would give the Mindset SDK a canvas its
 * own preview never reaches and leave a live camera attached to an invisible element, so the screen
 * renders exactly the one its scan is about to use.
 *
 * The hybrid vendor is the exception that proves the rule rather than breaking it: it runs both SDKs,
 * so it renders both elements at full size and stacks them - because the alternative, hiding one, is
 * precisely the failure the paragraph above describes (docs/decisions.md D-37).
 */
export function ScanningScreen({ troubles, progress, phase, vendor, previewAspect }: Props) {
  const { t } = useI18n()

  /**
   * The vendor's position, as a whole percent, or null for NO POSITION AT ALL - which is what zero is.
   *
   * Clamped rather than trusted, and rounded to whole percent because that is the only precision
   * anything downstream of this line can use: a two-pixel-wide difference in a bar, and an
   * `aria-valuenow` a screen reader reads out loud.
   *
   * **AND ZERO IS FOLDED INTO NULL, which is the whole of the fault behind "there is no progress
   * bar".** A fill of `width: 0%` is nothing at all inside an empty track: a working scan and a
   * broken one drew the same picture. Two ordinary things hand this a zero - shenai-session.ts hands
   * the bar back with `onProgress(0)` when the download finishes and the measurement takes over, and
   * the vendor answers zero for as long as it has not accumulated enough signal to claim any, which
   * on a face that keeps leaving the circle is most of a scan. Neither is the statement "0% through";
   * both are "nothing claimed yet", and this screen already has a shape for that - the indeterminate
   * pulse (docs/decisions.md D-53).
   */
  const reported = progress === null ? null : Math.round(Math.min(1, Math.max(0, progress)) * 100)
  const percent = reported === 0 ? null : reported

  /**
   * **GREEN IS EVIDENCE AND SO IS RED, so neither is the default.** Green is the vendor saying it is
   * measuring; red is the vendor saying something is wrong. Neither is true of the frames before it
   * has said anything at all - the seconds of loading and licensing, a session that died without
   * raising a signal, and the whole of the FaceHeart path, which reports no phase
   * (fh-vitals-session.ts). Those get the neutral card.
   *
   * Red briefly covered them too, and that is exactly what a patient reads as a scan that has
   * already gone wrong, on a screen they are being asked to hold still in front of for a minute
   * (docs/decisions.md D-50).
   */
  /**
   * The vendor's own bar, full, with the scan not yet over.
   *
   * It is the whole of the gap the patient sees as a stuck screen: the poll loop ends, the vendor
   * finalizes its models (`stopMeasurement`, `results`), and the reading goes to the service - and
   * `progress` is left at 1 through all of it, because nothing resets it until this screen is
   * replaced. So the full bar IS the signal, and no new one had to be invented for it.
   */
  const finishing = percent === 100

  const scanState: ScanState = finishing
    ? 'good'
    : troubles.length > 0
      ? 'trouble'
      : phase === 'measuring'
        ? 'good'
        : 'preparing'

  /**
   * ONE LINE, AND EVERY STATE IN IT. The screen used to carry two - a standing instruction above the
   * preview and the vendor's signals below it - which is two places to look while holding a phone
   * still at arm's length, and the lower one was off the bottom of a small screen exactly when it had
   * something to say.
   *
   * FINISHING WINS OVER ALL OF IT, including the vendor's signals. A framing complaint raised on the
   * last poll of a finished measurement is not something to act on any more - "move the phone closer"
   * to a patient whose scan is already over is an instruction that cannot help and a red card for a
   * scan that worked. The colour goes with it, for the same reason.
   *
   * Otherwise the signal wins, because it is the only line here the patient can act on. It is
   * the FIRST of them rather than all: the vendor raises several at once and this is one line, so the
   * others wait for the one in front of them to be fixed. That is a real loss against what was here
   * before (docs/decisions.md D-28) and it is the trade one line asks for.
   *
   * Otherwise one of two, and no more (docs/decisions.md D-50). Once the vendor is measuring, hold
   * still. Until it is, the same line the permission screen showed - the camera question is what
   * this stretch is about, and carrying that line across the two screens means the words do not
   * change under a patient who has just been asked something. The loading and licensing phases still
   * drive the stall watchdog (scan-flow.tsx); they are no longer announced.
   */
  const message = finishing
    ? t('finalizing')
    : troubles[0] !== undefined
      ? t(troubles[0].messageKey)
      : phase === 'measuring'
        ? t('measuringHoldStill')
        : t('cameraPermission')

  /**
   * What the Shen.AI canvas has to be scaled by for the camera to FILL the frame rather than sit
   * letterboxed inside it.
   *
   * The vendor draws the camera into a canvas it owns, keeping the camera's aspect ratio and filling
   * the rest with its own background - which on a portrait camera in this portrait frame is a white
   * bar down each side. `object-fit` cannot reach it: that crops a canvas by its DRAWING BUFFER, and
   * the vendor sizes the buffer to the element, so the letterbox is painted inside the bitmap rather
   * than around it. Scaling the element pushes those bars past the frame's edge and
   * `overflow-hidden` cuts them off.
   *
   * The factor is the one `object-fit: cover` would compute, in whichever direction the camera is the
   * wrong shape: 1 when the two ratios agree, and never less. Some of the camera goes off the edge,
   * which is the trade a preview makes to be a preview rather than a letterbox.
   */
  const previewCover =
    previewAspect && previewAspect > 0
      ? Math.max(FRAME_ASPECT / previewAspect, previewAspect / FRAME_ASPECT)
      : 1

  return (
    <Card
      data-scan-state={scanState}
      className={cn(
        'relative flex flex-col items-center gap-4 overflow-hidden transition-colors duration-700',
        scanState === 'trouble' && 'border-red-500',
        scanState === 'good' && 'border-green-500',
      )}
    >
      <ScanStatusWash state={scanState} />

      {/*
        * The one thing to read, directly above the one thing to look at, with the bar under it - so a
        * patient holding a phone at arm's length has a single place to check rather than three.
        *
        * Its colour is the card's, which is the point: black while this page is still waiting, green
        * while the vendor is measuring and content, red when it is not.
        */}
      <p
        role="status"
        className={cn(
          'relative min-h-14 whitespace-pre-line text-center text-lg font-semibold',
          scanState === 'trouble' && 'text-red-800',
          scanState === 'good' && 'text-green-800',
          scanState === 'preparing' && 'text-slate-900',
        )}
      >
        {message}
      </p>

      {/**
       * **A bar, and deliberately no clock.** The vendor emits `timeLeft` once a second carrying both
       * a countdown and a `percentComplete`, and only the second one is read (mindset-session.ts).
       * Showing a patient "38s remaining" invites them to watch it, and it is a promise this app
       * cannot keep in either direction: the SDK ends a session as soon as its models converge, and
       * the app's own 110-second window is what bounds one that never does, so the number would be
       * wrong early on a good scan and wrong late on a bad one. A bar makes the same statement - this
       * is running, this is how far in - and makes no claim about when it ends.
       *
       * Nothing numeric is rendered, not even the percentage the prototype showed beside its bar. The
       * position is in `aria-valuenow` where a screen reader can read it on request; on the screen it
       * is a length, because a digit on this screen is something for the patient to do arithmetic
       * with while trying to hold still.
       *
       * **Indeterminate when the vendor has said nothing.** With no `aria-valuenow` and a pulsing
       * third of the track, which is the honest shape for a FaceHeart scan: that SDK reports state
       * names, not progress (fh-vitals-events.ts), and the alternative - animating a bar off a wall
       * clock against a session length neither vendor commits to - is a fabricated measurement of
       * somebody else's process, shown to a patient, on a medical page.
       */}
      <div
        role="progressbar"
        aria-label={t('scanProgress')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent ?? undefined}
        className="relative h-2 w-full overflow-hidden rounded-full bg-slate-200"
      >
        <div
          className={cn(
            'h-full rounded-full bg-slate-900 transition-[width] duration-700 ease-out',
            percent === null && 'w-1/3 animate-pulse',
          )}
          style={percent === null ? undefined : { width: `${percent}%` }}
        />
      </div>

      {vendor === 'HybridMindsetShenAi' ? (
        /**
         * TWO SURFACES IN ONE FRAME, because a hybrid scan runs two SDKs that want different
         * elements (docs/decisions.md D-37): Mindset reads frames off a `<video>` this app puts a
         * camera on, and Shen.AI renders into a `<canvas>` it is handed the id of and runs its
         * WebGL2 pipeline on.
         *
         * **NEITHER MAY BE `display: none`, AND THAT IS THE WHOLE DESIGN OF THIS BRANCH.** A video
         * that is not laid out is a video whose `requestVideoFrameCallback` a browser is entitled to
         * stop calling - which is Mindset measuring nothing - and a canvas that is not laid out has
         * no backing store for Shen.AI to draw into. Both failures are silent: the vendor reports a
         * face it can see and a measurement that produced nothing, which is indistinguishable from a
         * patient who moved (D-35, D-36). So both elements are full-size and in flow, and the video
         * is simply STACKED ON TOP of the canvas - occluded, still painted, still decoding.
         *
         * The video is the one the patient sees because it is the surface this app owns: mirrored,
         * under this app's own guide, with no vendor drawing in it. Shen.AI's own interface is
         * switched off at initialize (browser-shenai-client.ts), so what is underneath is its preview
         * of the same face and nothing else - which nobody is looking at, and which the vendor still
         * needs to be drawing.
         */
        <div className="relative aspect-[315/382] w-full overflow-hidden rounded-xl bg-black">
          <canvas id={SCAN_CANVAS_ID} className="absolute inset-0 h-full w-full object-cover" />
          <video
            id={SCAN_VIDEO_ID}
            muted
            playsInline
            className="absolute inset-0 h-full w-full scale-x-[-1] object-cover"
          />
          <FaceGuideMask className="pointer-events-none absolute inset-0 h-full w-full" />
        </div>
      ) : vendor === 'ShenAi' ? (
        /**
         * Shen renders the preview ITSELF, onto a canvas it is handed the id of - so this is the
         * Mindset frame with a canvas in place of the video, and the same guide drawn over it. The
         * vendor's own interface is switched off at initialize (browser-shenai-client.ts), so what
         * lands on this canvas is the camera and nothing else: no overlay, no warnings, no logo.
         *
         * No `scale-x-[-1]` on the canvas, unlike the video under it. Shen mirrors its own preview,
         * and flipping it again in CSS would show the patient a scan of themselves reversed twice -
         * which reads as correct until they raise a hand.
         *
         * **AND NO VIDEO BESIDE IT, WHICH IS A RULE AND NOT AN OMISSION.** One camera has one consumer,
         * and on this vendor that consumer is the SDK: the stream this app opens in the patient's tap
         * (D-24, D-51) is handed over with `setMediaStream` and nothing else reads it. A `<video>` was
         * put here for one day, on the theory that the gap between the tap and the vendor's first
         * frame was D-25 - "a track held open with nothing consuming a frame". It is not: D-25 records
         * that the track stays LIVE and it is the PREVIEW that goes black. So the element bought
         * nothing and cost frames off the one stream the vendor measures from, on a phone, where the
         * frame rate is the measurement.
         *
         * The hybrid branch above is not an exception to this: it runs two SDKs and gives each its own
         * handle, one CLONE off one `getUserMedia` (docs/decisions.md D-37), rather than two readers
         * on one track.
         */
        <div className="relative aspect-[315/382] w-full overflow-hidden rounded-xl bg-black">
          <canvas
            id={SCAN_CANVAS_ID}
            className="absolute inset-0 h-full w-full object-cover"
            style={previewCover > 1 ? { transform: `scale(${previewCover})` } : undefined}
          />
          <FaceGuideMask className="pointer-events-none absolute inset-0 h-full w-full" />
        </div>
      ) : vendor === 'Mindset' ? (
        /**
         * The preview frame, ported from the prototype's `VideoPreviewContainer`: a 315x382 portrait
         * box, black behind the video so the first tenth of a second before frames arrive is not a
         * white flash, and `overflow-hidden` so the rounded corners actually clip the video.
         *
         * **On the dimensions, because the question of what the SDK depends on is worth answering
         * exactly.** The vendor does NOT read this element's box. Its frame pump is
         * `createImageBitmap(video, 0, 0, video.videoWidth, video.videoHeight)` followed by
         * `detectVitalsJS(bitmap, video.videoWidth, video.videoHeight, …)` - the INTRINSIC size of
         * the stream, which browser-mindset-client.ts asks the camera for as 640x480 and which no CSS
         * here can change. So the `width={320} height={240}` attributes this element used to carry
         * are gone: they described nothing the SDK read and nothing the patient saw (Tailwind's
         * `h-full w-full` overrode them), and an attribute that looks like a vendor requirement is
         * worse than no attribute at all, because the next person to touch this file has to prove it
         * is not one. The prototype set no dimensions on its video either, for the same reason.
         *
         * The frame's aspect ratio, on the other hand, is load-bearing - just not for the SDK. It is
         * 315x382 because face-guide-mask.tsx's artwork is drawn on that field, and a circle drawn
         * for a portrait frame in a landscape one lands on the patient's shoulder.
         *
         * `object-cover` crops a 640x480 landscape stream to that portrait box rather than
         * letterboxing it, which is what makes the guide's head-and-shoulders framing mean anything
         * on a phone held at arm's length. The SDK still gets all 640x480; the crop is the patient's
         * viewport onto the stream, not the stream.
         *
         * `scale-x-[-1]` mirrors the preview, as the prototype does. A patient moving left and
         * watching their image move right cannot centre their own face - a preview is a mirror or it
         * is an obstacle. This is also invisible to the vendor: a CSS transform does not touch what
         * `createImageBitmap` reads out of the element, so the models see the unmirrored camera frame
         * whatever the patient sees.
         */
        <div className="relative aspect-[315/382] w-full overflow-hidden rounded-xl bg-black">
          <video
            id={SCAN_VIDEO_ID}
            muted
            playsInline
            className="h-full w-full scale-x-[-1] object-cover"
          />
          <FaceGuideMask className="pointer-events-none absolute inset-0 h-full w-full" />
        </div>
      ) : (
        /**
         * FaceHeart's surface is deliberately left exactly as it was, guide included - which is to
         * say, without one.
         *
         * The prototype gave its FaceHeart canvas the same portrait frame and the same guide, at
         * `width={315} height={382}` (`FaceHeartMonitorV2.tsx`). Copying that here would mean
         * resizing the backing store a vendor SDK draws into and reads back from, on the one vendor
         * path whose only proof is a Playwright journey that drives the real SDK against recorded
         * footage - and a change to a vendor's own pipeline needs a reason better than visual
         * symmetry with the other vendor's screen. 320x240 is what this integration was built and
         * measured against, so 320x240 it stays until somebody has a FaceHeart scan quality question
         * to answer.
         */
        <canvas id={SCAN_CANVAS_ID} width={320} height={240} className="rounded-lg bg-slate-900" />
      )}


    </Card>
  )
}
